import React, { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { useGetCartExport } from 'api';
import WiseCartImportModal from 'components/wise/WiseCartImportModal';

import styles from 'theme/components/wise/WiseCartActionsBar/WiseCartActionsBar.module.scss';

export type WiseCartActionsBarProps = {
  cartId: number;
  cartName?: string;
  onChange?: () => void;
};

const WiseCartActionsBar: FC<WiseCartActionsBarProps> = ({ cartId, cartName, onChange }) => {
  const { t } = useTranslation();
  const [importOpen, setImportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { refetch } = useGetCartExport(
    cartId,
    { exportType: 'csv' },
    {
      enabled: false,
      retry: false,
    }
  );

  const handleExport = useCallback(async () => {
    if (isExporting) {
      return;
    }
    setIsExporting(true);
    try {
      const result = await refetch();
      const data = result.data;
      if (!data || !data.content || !data.file_name) {
        return;
      }
      const linkSource = `data:text/csv;base64,${data.content}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = data.file_name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } finally {
      setIsExporting(false);
    }
  }, [refetch, isExporting]);

  const handleImportSuccess = useCallback(() => {
    setImportOpen(false);
    if (onChange) {
      onChange();
    }
  }, [onChange]);

  return (
    <>
      <div
        className={classnames(
          styles.actionsBar,
          'StylePath-Components-Wise-WiseCartActionsBar'
        )}
        data-cart-name={cartName}>
        <button
          type="button"
          className={styles.button}
          onClick={handleExport}
          disabled={isExporting}>
          <Download size={16} aria-hidden />
          <span>{t('Eksportuj koszyk')}</span>
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={() => setImportOpen(true)}>
          <Upload size={16} aria-hidden />
          <span>{t('Importuj do koszyka')}</span>
        </button>
      </div>

      <WiseCartImportModal
        open={importOpen}
        cartId={cartId}
        onClose={() => setImportOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};

export default WiseCartActionsBar;
